import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { FileText, Folder } from 'lucide-react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command'
import { getAreaTree, areaKeys } from '@/lib/api/areas'
import { listDocuments, documentKeys } from '@/lib/api/documents'
import { flattenAllAreas, type FlatArea } from '@/lib/areaTree'
import { matchRank, highlightMatch } from '@/lib/searchMatch'

const NODE_PAGE_SIZE = 6
const FILE_PAGE_SIZE = 6

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SearchCommandPopup({ open, onOpenChange }: Props) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [nodeLimit, setNodeLimit] = useState(NODE_PAGE_SIZE)
  const [highlightedValue, setHighlightedValue] = useState('')

  const { data: tree = [] } = useQuery({ queryKey: areaKeys.tree(), queryFn: getAreaTree })
  const flatAreas = useMemo(() => flattenAllAreas(tree), [tree])
  const areaLookup = useMemo(() => new Map(flatAreas.map((a) => [a.id, a])), [flatAreas])

  const trimmedQuery = query.trim()
  const isSearching = trimmedQuery.length >= 2

  const rankedNodes = useMemo(() => {
    if (!isSearching) return []
    return flatAreas
      .map((area) => ({ area, rank: matchRank(area.name, trimmedQuery) }))
      .filter((x): x is { area: FlatArea; rank: 0 | 1 } => x.rank !== null)
      .sort((a, b) => a.rank - b.rank || a.area.name.localeCompare(b.area.name))
      .map((x) => x.area)
  }, [flatAreas, trimmedQuery, isSearching])

  const visibleNodes = rankedNodes.slice(0, nodeLimit)
  const hasMoreNodes = rankedNodes.length > visibleNodes.length

  const [debouncedQuery, setDebouncedQuery] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(trimmedQuery), 250)
    return () => clearTimeout(timer)
  }, [trimmedQuery])

  const isSearchingFiles = debouncedQuery.length >= 2
  const fileSearchParams = { search: debouncedQuery, pageSize: FILE_PAGE_SIZE }
  const filesQuery = useInfiniteQuery({
    queryKey: documentKeys.list(fileSearchParams),
    initialPageParam: 1,
    queryFn: ({ pageParam }) => listDocuments({ ...fileSearchParams, page: pageParam as number }),
    getNextPageParam: (lastPage, allPages) =>
      allPages.length * FILE_PAGE_SIZE < lastPage.totalCount ? allPages.length + 1 : undefined,
    enabled: isSearchingFiles,
  })
  const fileItems = filesQuery.data?.pages.flatMap((p) => p.items) ?? []
  const hasMoreFiles = filesQuery.hasNextPage ?? false
  const visibleFileItems = fileItems.filter((doc) => areaLookup.has(doc.areaId))

  function handleOpenChange(next: boolean) {
    if (!next) {
      setQuery('')
      setNodeLimit(NODE_PAGE_SIZE)
      setHighlightedValue('')
    }
    onOpenChange(next)
  }

  function goToNode(area: FlatArea) {
    navigate(`/areas/${area.slug}`)
    handleOpenChange(false)
  }

  function goToFile(doc: { id: string; areaId: string }, areaInfo: FlatArea | undefined, openFile: boolean) {
    if (!areaInfo) return
    navigate(openFile ? `/areas/${areaInfo.slug}?doc=${doc.id}` : `/areas/${areaInfo.slug}`)
    handleOpenChange(false)
  }

  // cmdk não distingue Enter de Shift+Enter — intercepta em fase de captura (antes do próprio
  // cmdk reagir ao Enter "puro") pra tratar Shift+Enter como "abrir a pasta, não o arquivo".
  function handleKeyDownCapture(e: React.KeyboardEvent) {
    if (e.key !== 'Enter' || !e.shiftKey) return
    e.preventDefault()
    e.stopPropagation()
    const [kind, id] = highlightedValue.split(':')
    if (kind === 'node') {
      const area = rankedNodes.find((a) => a.id === id)
      if (area) goToNode(area)
    } else if (kind === 'file') {
      const doc = fileItems.find((d) => d.id === id)
      if (doc) goToFile(doc, areaLookup.get(doc.areaId), false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="flex max-w-[626px] flex-col gap-0 p-0"
        style={{ top: '15%', translate: '-50% 0', height: '80vh' }}
      >
        <DialogTitle className="sr-only">Buscar área ou arquivo</DialogTitle>
        <div onKeyDownCapture={handleKeyDownCapture} className="flex min-h-0 flex-1 flex-col">
          <Command
            shouldFilter={false}
            value={highlightedValue}
            onValueChange={setHighlightedValue}
            className="min-h-0 flex-1"
          >
            <CommandInput
              placeholder="Buscar área ou arquivo em todo o acervo"
              value={query}
              onValueChange={(value) => {
                setQuery(value)
                setNodeLimit(NODE_PAGE_SIZE)
              }}
              className="text-[length:var(--fs-small)]"
            />
            <CommandList className="max-h-none min-h-0 flex-1">
              {!isSearching && (
                <div className="px-4 py-6 text-[length:var(--fs-small)] text-muted-foreground">
                  <p>
                    Digite pelo menos 2 caracteres. A busca cobre nome de área e nome de arquivo,
                    sem acento, em {flatAreas.length} áreas.
                  </p>
                  <p className="mt-2">
                    Ex.: <strong>reuniao ordinaria</strong>, <strong>parecer</strong>,{' '}
                    <strong>17-06-2026</strong>, <strong>previc</strong>
                  </p>
                </div>
              )}

              {isSearching && rankedNodes.length === 0 && fileItems.length === 0 && !filesQuery.isLoading && (
                <CommandEmpty className="text-[length:var(--fs-small)]">
                  Nenhum resultado para &quot;{trimmedQuery}&quot;.
                </CommandEmpty>
              )}

              {isSearching && visibleNodes.length > 0 && (
                <CommandGroup heading="PASTAS">
                  {visibleNodes.map((area) => {
                    const { before, match, after } = highlightMatch(area.name, trimmedQuery)
                    return (
                      <CommandItem
                        key={area.id}
                        value={`node:${area.id}`}
                        onSelect={() => goToNode(area)}
                        className="text-[length:var(--fs-small)]"
                      >
                        <Folder className="mr-2 h-4 w-4 shrink-0 opacity-60" />
                        <div className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate">
                            {before}
                            {match && <strong>{match}</strong>}
                            {after}
                          </span>
                          {area.path && (
                            <span className="truncate text-[length:var(--fs-micro)] text-muted-foreground">
                              {area.path}
                            </span>
                          )}
                        </div>
                        <span className="ml-2 shrink-0 text-[length:var(--fs-micro)] text-muted-foreground">
                          {area.documentCount} arq.
                        </span>
                      </CommandItem>
                    )
                  })}
                  {hasMoreNodes && (
                    <button
                      type="button"
                      className="w-full px-2 py-1.5 text-center text-[length:var(--fs-small)] text-muted-foreground hover:text-foreground"
                      onClick={() => setNodeLimit((n) => n + NODE_PAGE_SIZE)}
                    >
                      Carregar mais
                    </button>
                  )}
                </CommandGroup>
              )}

              {isSearchingFiles && visibleFileItems.length > 0 && (
                <CommandGroup heading="ARQUIVOS">
                  {visibleFileItems.map((doc) => {
                    const areaInfo = areaLookup.get(doc.areaId)
                    const { before, match, after } = highlightMatch(doc.name, debouncedQuery)
                    return (
                      <CommandItem
                        key={doc.id}
                        value={`file:${doc.id}`}
                        onSelect={() => goToFile(doc, areaInfo, true)}
                        className="text-[length:var(--fs-small)]"
                      >
                        <FileText className="mr-2 h-4 w-4 shrink-0 opacity-60" />
                        <div className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate">
                            {before}
                            {match && <strong>{match}</strong>}
                            {after}
                          </span>
                          {areaInfo && (
                            <span className="truncate text-[length:var(--fs-micro)] text-muted-foreground">
                              {[areaInfo.path, areaInfo.name].filter(Boolean).join(' › ')}
                            </span>
                          )}
                        </div>
                        <span className="ml-2 shrink-0 rounded bg-muted px-1.5 py-0.5 text-[length:var(--fs-micro)]">
                          {doc.typeName}
                        </span>
                      </CommandItem>
                    )
                  })}
                  {hasMoreFiles && (
                    <button
                      type="button"
                      className="w-full px-2 py-1.5 text-center text-[length:var(--fs-small)] text-muted-foreground hover:text-foreground"
                      onClick={() => filesQuery.fetchNextPage()}
                    >
                      Carregar mais
                    </button>
                  )}
                </CommandGroup>
              )}
            </CommandList>
            <div className="flex items-center gap-3 border-t px-3 py-2 text-[length:var(--fs-micro)] text-muted-foreground">
              <span>↑↓ navegar</span>
              <span>Enter abrir</span>
              <span>⇧Enter abrir a pasta</span>
              <span>Esc fechar</span>
            </div>
          </Command>
        </div>
      </DialogContent>
    </Dialog>
  )
}
